import { useEffect } from "react";
import { getPageGroup, trackEvent, trackPageView } from "../lib/analytics";

const SCROLL_DEPTHS = [25, 50, 75, 90];

function getUtmParams(search) {
  const params = new URLSearchParams(search || "");

  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
  };
}

function getSectionTitle(element) {
  if (element.dataset.sectionTitle) return element.dataset.sectionTitle.trim();

  const heading = element.querySelector("h1, h2, h3, [data-section-heading]");
  return heading?.textContent?.replace(/\s+/g, " ").trim() || "";
}

function getSectionId(element, index) {
  if (element.dataset.sectionId) return element.dataset.sectionId;
  if (element.id) return element.id;

  const title = getSectionTitle(element)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return title || `section-${index + 1}`;
}

function getScrollPercent() {
  const documentElement = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || window.pageYOffset || documentElement.scrollTop || 0;
  const scrollHeight = Math.max(
    body?.scrollHeight || 0,
    documentElement?.scrollHeight || 0,
    body?.offsetHeight || 0,
    documentElement?.offsetHeight || 0
  );
  const viewportHeight = window.innerHeight || documentElement.clientHeight || 0;

  if (scrollHeight <= viewportHeight) return 0;

  return Math.min(100, Math.round(((scrollTop + viewportHeight) / scrollHeight) * 100));
}

function getPath(location) {
  return location?.pathname || window.location.pathname || "/";
}

export default function usePageAnalytics(location, locale) {
  const path = getPath(location);
  const search = location?.search || "";
  const routeKey = `${path}${search}`;

  useEffect(() => {
    trackPageView(path, {
      locale: locale || document.documentElement.lang || "",
      referrer: document.referrer || "",
      ...getUtmParams(search),
    });
  }, [path, search, locale]);

  useEffect(() => {
    const seenDepths = new Set();
    let ticking = false;

    const fireDepths = () => {
      ticking = false;
      const depth = getScrollPercent();

      SCROLL_DEPTHS.forEach((threshold) => {
        if (depth >= threshold && !seenDepths.has(threshold)) {
          seenDepths.add(threshold);
          trackEvent("scroll_depth", {
            path,
            depth: threshold,
          });
        }
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(fireDepths);
      } else {
        window.setTimeout(fireDepths, 16);
      }
    };

    const onLenisScroll = () => onScroll();
    const unsubscribeLenis =
      typeof window.lenis?.on === "function"
        ? window.lenis.on("scroll", onLenisScroll)
        : undefined;

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (typeof unsubscribeLenis === "function") {
        unsubscribeLenis();
      } else if (typeof window.lenis?.off === "function") {
        window.lenis.off("scroll", onLenisScroll);
      }
    };
  }, [path, routeKey]);

  useEffect(() => {
    let observer;
    let frameId;
    const seenSections = new Set();
    const seenLeadMagnets = new Set();
    const pageGroup = getPageGroup(path);

    const trackSection = (element, index) => {
      const sectionId = getSectionId(element, index);
      if (seenSections.has(sectionId)) return;

      seenSections.add(sectionId);
      trackEvent("section_view", {
        path,
        section_id: sectionId,
        section_title: getSectionTitle(element),
        page_group: pageGroup,
      });
    };

    const trackLeadMagnet = (element) => {
      const assetKey = element.dataset.leadMagnetSection || "";
      if (!assetKey || seenLeadMagnets.has(assetKey)) return;

      seenLeadMagnets.add(assetKey);
      trackEvent("lead_magnet_view", {
        path,
        asset_key: assetKey,
        route: element.dataset.leadMagnetRoute || "",
        niche: element.dataset.leadMagnetNiche || assetKey,
      });
    };

    const setupObservers = () => {
      const sectionElements = Array.from(
        document.querySelectorAll("[data-track-section='true']")
      );
      const leadMagnetElements = Array.from(
        document.querySelectorAll("[data-lead-magnet-section]")
      );

      if (typeof IntersectionObserver !== "function") {
        sectionElements.forEach(trackSection);
        leadMagnetElements.forEach(trackLeadMagnet);
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const target = entry.target;
            const sectionIndex = sectionElements.indexOf(target);

            if (target.dataset.trackSection === "true") {
              trackSection(target, sectionIndex);
            }

            if (target.dataset.leadMagnetSection) {
              trackLeadMagnet(target);
            }

            observer.unobserve(target);
          });
        },
        {
          rootMargin: "0px 0px -20% 0px",
          threshold: 0.28,
        }
      );

      [...sectionElements, ...leadMagnetElements].forEach((element) =>
        observer.observe(element)
      );
    };

    if (typeof window.requestAnimationFrame === "function") {
      frameId = window.requestAnimationFrame(setupObservers);
    } else {
      setupObservers();
    }

    return () => {
      if (frameId && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(frameId);
      }
      observer?.disconnect();
    };
  }, [path, routeKey]);
}
